import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, X, Check, Radio, ImagePlus, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminShows() {
  const [shows, setShows] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', host_id: '', genre: 'Gospel', image_url: '' });
  const [uploadingImage, setUploadingImage] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const imageRef = useRef(null);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/shows`),
      axios.get(`${API}/hosts`)
    ]).then(([s, h]) => {
      setShows(s.data.shows || []);
      setHosts(h.data.hosts || []);
    }).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/shows/${editing}`, form);
        toast.success('Show updated');
      } else {
        await axios.post(`${API}/admin/shows`, form);
        toast.success('Show created');
      }
      setShowForm(false); setEditing(null);
      setForm({ name: '', description: '', host_id: '', image_url: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    setConfirmingDelete(id);
  };

  const executeDelete = async () => {
    if (!confirmingDelete) return;
    try {
      await axios.delete(`${API}/admin/shows/${confirmingDelete}`);
      toast.success('Show deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
    setConfirmingDelete(null);
  };

  const startEdit = (show) => {
    setForm({ name: show.name, description: show.description, host_id: show.host_id, image_url: show.image_url || '' });
    setEditing(show.id);
    setShowForm(true);
  };

  const handleImageUpload = async (showId, file) => {
    setUploadingImage(showId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/admin/shows/${showId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Show image uploaded');
      fetchData();
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleGenerateImage = async (showId) => {
    setGeneratingImage(showId);
    setShowPromptModal(null);
    try {
      await axios.post(`${API}/admin/shows/${showId}/generate-image`, { prompt: aiPrompt }, { timeout: 120000 });
      toast.success('AI cover image generated!');
      setAiPrompt('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setGeneratingImage(null);
    }
  };

  const getHostName = (id) => hosts.find(h => h.id === id)?.name || '—';

  return (
    <div data-testid="admin-shows">
      {/* Delete Confirmation Modal */}
      {confirmingDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmingDelete(null)}>
          <div data-testid="delete-confirm-modal" className="bg-[#171A21] rounded-2xl border border-white/10 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500/10 mx-auto mb-4">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h3 className="text-white font-semibold text-lg text-center mb-2">Delete Show</h3>
            <p className="text-[#94A3B8] text-sm text-center mb-6">Are you sure? This cannot be undone.</p>
            <div className="flex gap-2">
              <button data-testid="delete-cancel-btn" onClick={() => setConfirmingDelete(null)} className="flex-1 bg-white/5 text-white rounded-full py-2.5 hover:bg-white/10 text-sm">Cancel</button>
              <button data-testid="delete-confirm-btn" onClick={executeDelete} className="flex-1 bg-red-500 text-white font-semibold hover:bg-red-600 rounded-full py-2.5 text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Prompt Modal */}
      {showPromptModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPromptModal(null)}>
          <div data-testid="ai-prompt-modal" className="bg-[#171A21] rounded-2xl border border-white/10 p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-[#D4AF37]/10"><Sparkles size={18} className="text-[#D4AF37]" /></div>
              <div>
                <h3 className="text-white font-semibold">Generate Cover Art</h3>
                <p className="text-[#94A3B8] text-xs">AI will create a unique image for this show</p>
              </div>
            </div>
            <textarea
              data-testid="ai-prompt-input"
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder="Optional: describe the style you want (e.g. 'vibrant African sunset with choir silhouettes')... Leave blank for auto-generated prompt."
              className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none w-full h-24 resize-none placeholder:text-slate-600 mb-4"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowPromptModal(null)} className="flex-1 bg-white/5 text-white rounded-full py-2.5 hover:bg-white/10 text-sm">Cancel</button>
              <button data-testid="ai-generate-confirm" onClick={() => handleGenerateImage(showPromptModal)} className="flex-1 bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full py-2.5 text-sm flex items-center justify-center gap-2">
                <Sparkles size={14} /> Generate
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Shows</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{shows.length} programs</p>
        </div>
        <button
          data-testid="add-show-button"
          onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: '', description: '', host_id: '', image_url: '' }); }}
          className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-6 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
        >
          <Plus size={18} /> Add Show
        </button>
      </div>

      {showForm && (
        <form data-testid="show-form" onSubmit={handleSubmit} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 mb-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Show Name *</label>
              <input
                data-testid="show-name-input"
                type="text"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder="e.g. Morning Glory"
                className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Host</label>
              <select
                data-testid="show-host-select"
                value={form.host_id}
                onChange={e => setForm({...form, host_id: e.target.value})}
                className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full"
              >
                <option value="">Select host...</option>
                {hosts.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Description</label>
            <textarea
              data-testid="show-description-input"
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Describe this show..."
              rows={3}
              className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 rounded-full px-6 py-2.5">Cancel</button>
            <button data-testid="show-submit-button" type="submit" className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5">{editing ? 'Update' : 'Create'} Show</button>
          </div>
        </form>
      )}

      {/* Shows Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shows.map((show, i) => (
          <div key={show.id} data-testid={`admin-show-${i}`} className="bg-[#171A21] rounded-2xl border border-white/5 overflow-hidden hover:-translate-y-0.5 transition-transform">
            {/* Show Image */}
            <div className="relative h-36 bg-gradient-to-br from-[#D4AF37]/10 to-[#991B1B]/10 flex items-center justify-center group">
              {(show.image_data && show.image_data.startsWith('data:')) ? (
                <img src={show.image_data} alt={show.name} className="w-full h-full object-cover" />
              ) : show.image_storage_path ? (
                <img src={`${API}/show-image/${show.id}`} alt={show.name} className="w-full h-full object-cover" />
              ) : (
                <Radio size={36} className="text-[#D4AF37]/30" />
              )}
              {generatingImage === show.id && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                  <Loader2 size={24} className="text-[#D4AF37] animate-spin mb-2" />
                  <span className="text-white text-xs font-medium">Generating...</span>
                </div>
              )}
              {generatingImage !== show.id && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                  <label className="cursor-pointer flex items-center gap-1.5 bg-white/10 text-white font-medium rounded-full px-3 py-2 text-xs hover:bg-white/20 transition-colors backdrop-blur">
                    {uploadingImage === show.id ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`upload-show-image-${i}`}
                      onChange={e => e.target.files[0] && handleImageUpload(show.id, e.target.files[0])}
                    />
                  </label>
                  <button
                    data-testid={`generate-show-image-${i}`}
                    onClick={() => { setShowPromptModal(show.id); setAiPrompt(''); }}
                    className="flex items-center gap-1.5 bg-[#D4AF37] text-[#0F1115] font-semibold rounded-full px-3 py-2 text-xs hover:bg-[#E8C24D] transition-colors active:scale-95"
                  >
                    <Sparkles size={12} /> AI Generate
                  </button>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white font-semibold text-lg">{show.name}</h3>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-show-${i}`} onClick={() => startEdit(show)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#94A3B8]"><Edit2 size={14} /></button>
                  <button data-testid={`delete-show-${i}`} onClick={() => handleDelete(show.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-[#94A3B8] text-sm line-clamp-2 mb-3">{show.description}</p>
              <p className="text-[#94A3B8] text-xs pt-3 border-t border-white/5">Host: <span className="text-white">{getHostName(show.host_id)}</span></p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
